import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import {
  ChartBarIcon,
  UserGroupIcon,
  DocumentTextIcon,
  ClipboardDocumentListIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { useAuthStore } from '../../stores/authStore';
import AnalyticsDashboardPage from './AnalyticsDashboardPage';
import LawsuitManagementPage from './LawsuitManagementPage';
import UserManagementPage from './UserManagementPage';
import AuditLogPage from './AuditLogPage';

type Tab = 'dashboard' | 'lawsuits' | 'users' | 'audit';

const AdminDashboardPage: React.FC = () => {
  const { t } = useTranslation();
  const { user, refreshUser } = useAuthStore();
  const [activeTab, setActiveTab] = useState<Tab>('dashboard');

  // Refresh user data on mount to ensure admin status is up-to-date
  useEffect(() => {
    const refreshUserData = async () => {
      try {
        await refreshUser();
      } catch (error) {
        console.error('Failed to refresh user data:', error);
      }
    };
    refreshUserData();
  }, [refreshUser]);

  const tabs = [
    {
      id: 'dashboard' as Tab,
      name: t('admin.dashboard', 'Dashboard'),
      icon: ChartBarIcon,
    },
    {
      id: 'lawsuits' as Tab,
      name: t('admin.lawsuits', 'Lawsuits'),
      icon: DocumentTextIcon,
    },
    {
      id: 'users' as Tab,
      name: t('admin.users', 'Users'),
      icon: UserGroupIcon,
    },
    {
      id: 'audit' as Tab,
      name: t('admin.auditLog', 'Audit Log'),
      icon: ClipboardDocumentListIcon,
    },
  ];

  // Check if user is admin
  if (!user || !user.isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-red-500" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            {t('admin.accessDenied', 'Access Denied')}
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            {t('admin.adminRequired', 'You need admin privileges to access this page.')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{t('admin.dashboard', 'Admin Dashboard')} - tvia.ai</title>
        <meta
          name="description"
          content={t('admin.dashboardDescription', 'Admin dashboard for managing the tvia.ai platform')}
        />
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900">
              {t('admin.adminPortal', 'Admin Portal')}
            </h1>
            <p className="mt-2 text-lg text-gray-600">
              {t('admin.portalDescription', 'Manage lawsuits, users, and system operations.')}
            </p>
          </div>

          {/* Tabs */}
          <div className="mb-6">
            <div className="sm:hidden">
              <label htmlFor="tabs" className="sr-only">
                {t('admin.selectTab', 'Select a tab')}
              </label>
              <select
                id="tabs"
                name="tabs"
                value={activeTab}
                onChange={(e) => setActiveTab(e.target.value as Tab)}
                className="block w-full rounded-md border-gray-300 focus:border-cyan-500 focus:ring-cyan-500"
              >
                {tabs.map((tab) => (
                  <option key={tab.id} value={tab.id}>
                    {tab.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="hidden sm:block">
              <nav className="flex space-x-4" aria-label="Tabs">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`${
                        activeTab === tab.id
                          ? 'bg-cyan-100 text-cyan-700'
                          : 'text-gray-500 hover:text-gray-700'
                      } px-3 py-2 font-medium text-sm rounded-md flex items-center space-x-2 transition-colors`}
                    >
                      <Icon className="h-5 w-5" />
                      <span>{tab.name}</span>
                    </button>
                  );
                })}
              </nav>
            </div>
          </div>

          {/* Tab Content */}
          <div className="mt-6">
            {activeTab === 'dashboard' && <AnalyticsDashboardPage />}
            {activeTab === 'lawsuits' && <LawsuitManagementPage />}
            {activeTab === 'users' && <UserManagementPage />}
            {activeTab === 'audit' && <AuditLogPage />}
          </div>
        </div>
      </div>
    </>
  );
};

export default AdminDashboardPage;
